import { ReactElement, Suspense, useMemo, useRef, useState } from 'react';
import {
  useApolloClient,
  useMutation,
  useSuspenseQuery,
} from '@apollo/client/react';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Alert, FullLoader, Modal, ModalFooter } from '@sc/shared-ui';
import env from '../environment';
import { notify } from '../utility/notify';
import { useAlerts } from '../providers/alerts';
import { useUser } from '../providers/users';
import { NewCardForm, NewCardFormHandle } from './NewCardForm';
import {
  ADD_NEW_PAYMENT_METHOD,
  PaymentMethodSelector,
} from './PaymentMethodSelector';
import {
  GQL_MUTATION_CREATE_SETUP_INTENT,
  GQL_MUTATION_PAY_INVOICE,
  GQL_MUTATION_SAVE_PAYMENT_METHOD,
  GQL_QUERY_LIST_INVOICES,
  GQL_QUERY_LIST_PAYMENT_METHODS,
  GQL_QUERY_READ_INVOICE,
} from '../utility/queries';

interface Props {
  open: boolean;
  onClose: () => void;
  invoiceUuid: string;
  /** The invoice total, used on the explicit pay button. */
  total: number | null;
}

const stripePromise = env.stripePublishableKey
  ? loadStripe(env.stripePublishableKey)
  : null;

const formatGbp = (value: number): string =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(value);

const PayInvoiceForm = ({
  invoiceUuid,
  total,
  onClose,
  isSubmitting,
  setIsSubmitting,
}: {
  invoiceUuid: string;
  total: number | null;
  onClose: () => void;
  isSubmitting: boolean;
  setIsSubmitting: (next: boolean) => void;
}): ReactElement => {
  const stripe = useStripe();
  const apolloClient = useApolloClient();
  const { user } = useUser();
  const { useApolloError } = useAlerts();
  const onError = useApolloError();
  const newCardRef = useRef<NewCardFormHandle>(null);

  const { data } = useSuspenseQuery(GQL_QUERY_LIST_PAYMENT_METHODS);
  const cards = data.listPaymentMethods;

  const [payInvoice] = useMutation(GQL_MUTATION_PAY_INVOICE, { onError });

  const [createSetupIntent] = useMutation(GQL_MUTATION_CREATE_SETUP_INTENT, {
    onError,
  });

  const [savePaymentMethod] = useMutation(GQL_MUTATION_SAVE_PAYMENT_METHOD, {
    onError,
    refetchQueries: [GQL_QUERY_LIST_PAYMENT_METHODS],
  });

  const defaultCard = cards.find((card) => card.useForMonthlyPlan) ?? cards[0];

  // With no saved cards, skip the picker and start on the new-card step.
  const [step, setStep] = useState<'new-card' | 'pick'>(
    cards.length > 0 ? 'pick' : 'new-card'
  );

  const [selected, setSelected] = useState<string>(
    cards.length > 0 ? (defaultCard?.uuid ?? '') : ADD_NEW_PAYMENT_METHOD
  );

  const [selectError, setSelectError] = useState<string | undefined>();

  const isAddNew = selected === ADD_NEW_PAYMENT_METHOD;

  // Returns true once the invoice is paid, running 3DS if Stripe asks for it.
  // The `invoice.paid` webhook settles the authoritative record a beat later;
  // refetch the open invoice views so the status catches up.
  const confirmInvoicePayment = async (card: {
    paymentMethodId?: string;
    paymentMethodUuid?: string;
  }): Promise<boolean> => {
    if (!stripe) return false;

    const { data: payData } = await payInvoice({
      variables: {
        input: {
          invoiceUuid,
          paymentMethodId: card.paymentMethodId ?? null,
          paymentMethodUuid: card.paymentMethodUuid ?? null,
        },
      },
    });

    const result = payData?.payInvoice;

    if (!result) return false;

    if (!result.succeeded) {
      if (!result.clientSecret) return false;

      const { paymentIntent, error } = await stripe.confirmCardPayment(
        result.clientSecret
      );

      if (error || paymentIntent.status !== 'succeeded') return false;
    }

    await apolloClient.refetchQueries({
      include: [GQL_QUERY_READ_INVOICE, GQL_QUERY_LIST_INVOICES],
    });

    return true;
  };

  const submit = async () => {
    if (!selected) {
      setSelectError('Choose a card to charge.');
      return;
    }

    if (isAddNew && step === 'pick') {
      setStep('new-card');
      return;
    }

    setIsSubmitting(true);

    try {
      let card: { paymentMethodId?: string; paymentMethodUuid?: string } = {
        paymentMethodUuid: selected,
      };

      if (isAddNew) {
        const { data: setupData } = await createSetupIntent();
        const setupClientSecret = setupData?.createSetupIntent.clientSecret;

        if (!setupClientSecret) return;

        const result = await newCardRef.current?.confirm(setupClientSecret);

        if (!result?.paymentMethodId) {
          if (result?.error) notify.error(result.error);
          return;
        }

        const { data: saved } = await savePaymentMethod({
          variables: {
            input: {
              paymentMethodId: result.paymentMethodId,
              paymentMethodUuid: null,
              useForMonthlyPlan: null,
              billingDetails: null,
            },
          },
        });

        if (!saved?.savePaymentMethod) return;

        card = { paymentMethodId: result.paymentMethodId };
      }

      if (await confirmInvoicePayment(card)) {
        notify.success('Invoice paid.');

        onClose();
      } else {
        notify.error('Payment failed. Please try another card.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();

        void submit();
      }}
    >
      <div className="p-5 space-y-4">
        {step === 'pick' && (
          <PaymentMethodSelector
            paymentMethods={cards}
            value={selected}
            onChange={(value) => {
              setSelected(value);

              setSelectError(undefined);
            }}
            error={selectError}
          />
        )}

        {step === 'new-card' && (
          <NewCardForm ref={newCardRef} defaultName={user.name ?? ''} />
        )}
      </div>
      <ModalFooter
        submitText={
          step === 'pick' && isAddNew
            ? 'Continue'
            : total !== null
              ? `Pay ${formatGbp(total)}`
              : 'Pay now'
        }
        asForm
        onClose={
          step === 'new-card' && cards.length > 0
            ? () => setStep('pick')
            : onClose
        }
        closeText={step === 'new-card' && cards.length > 0 ? 'Back' : 'Cancel'}
        buttonsDisabled={isSubmitting || !stripe}
      />
    </form>
  );
};

/**
 * Settles an open (or late) invoice: pick a saved card (or add a new one) and
 * pay, with 3DS if Stripe asks for it. The trigger button lives with the
 * caller — this is just the modal, following the usual button → modal →
 * loader → action shape.
 */
export const PayInvoiceModal = ({
  open,
  onClose,
  invoiceUuid,
  total,
}: Props): ReactElement => {
  // Re-mount Elements per opening so the form state resets between attempts.
  const elementsKey = useMemo(() => (open ? Date.now() : 0), [open]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <Modal
      title="Pay invoice"
      open={open}
      // Block closing (backdrop, escape, X) while the payment is in flight.
      onClose={isSubmitting ? undefined : onClose}
      loading={isSubmitting}
      dialogPanelClassname="w-120"
    >
      {stripePromise ? (
        <Elements
          key={elementsKey}
          stripe={stripePromise}
          options={{ appearance: { theme: 'night' } }}
        >
          <Suspense
            fallback={
              <div className="p-10 flex justify-center">
                <FullLoader className="size-6" />
              </div>
            }
          >
            <PayInvoiceForm
              invoiceUuid={invoiceUuid}
              total={total}
              onClose={onClose}
              isSubmitting={isSubmitting}
              setIsSubmitting={setIsSubmitting}
            />
          </Suspense>
        </Elements>
      ) : (
        <div className="p-5">
          <Alert type="serious" message="Something went wrong." />
        </div>
      )}
    </Modal>
  );
};
